import type { Data } from '@puckeditor/core';
import { useQuery } from '@apollo/client';
import { useRouter } from 'next/compat/router';

import { CmsContentVersionDocument } from 'src/gql/graphql';
import { isPuckData } from './puck-utils';
import { parseAsPathSearchParams } from './parse-as-path-search-params';

export const CMS_PREVIEW_MODEL_ID_QUERY_PARAM = 'cmsModelId';
export const CMS_PREVIEW_VERSION_ID_QUERY_PARAM = 'cmsVersionId';

/**
 * Extracts the optional CMS version preview ID from URL search params, ONLY if:
 * 1. Both `cmsModelId` and `cmsVersionId` are present exactly once as non-empty strings.
 * 2. `cmsModelId` exactly matches `expectedModelId`.
 *
 * If either param is missing, repeated, empty, or if `cmsModelId` does not match
 * `expectedModelId`, returns `undefined`.
 *
 * Takes `URLSearchParams` rather than `router.query` -- see `useCmsPreviewVersionId`'s doc
 * comment for why `router.query` is not a trustworthy source for this.
 */
export function getMatchingPreviewVersionId(
  searchParams: URLSearchParams | undefined,
  expectedModelId: string
): string | undefined {
  if (!searchParams) return undefined;

  const modelIds = searchParams.getAll(CMS_PREVIEW_MODEL_ID_QUERY_PARAM);
  const versionIds = searchParams.getAll(CMS_PREVIEW_VERSION_ID_QUERY_PARAM);

  // getAll returns [] when absent and >1 entries for a repeated param -- both are rejected the
  // same way a missing/array-valued router.query entry used to be.
  if (modelIds.length !== 1 || versionIds.length !== 1) {
    return undefined;
  }

  const [rawModelId] = modelIds;
  const [rawVersionId] = versionIds;

  if (!rawModelId || !rawVersionId || rawModelId.trim() !== rawModelId || rawVersionId.trim() !== rawVersionId) {
    return undefined;
  }

  if (rawModelId !== expectedModelId) {
    return undefined;
  }

  return rawVersionId;
}

/**
 * Reads the active preview version ID for a given model from the real URL, not `router.query`.
 *
 * `router.query` is populated from Next's hydration data (`__NEXT_DATA__`), which for this
 * route (`/stores/[cName]/cms/[...slug]`, served through `getDispensaryServerSideProps` with
 * `cacheResponse: true`) is a CDN-cached SSR response: the Cache-Tag scheme keys the cached HTML
 * by `dispensary-<id>` only, NOT by the full URL including query string, so a request for this
 * page with no preview params (or a DIFFERENT version's params) can be served a cached response
 * whose baked-in `__NEXT_DATA__.query` still has a stale/unrelated `cmsModelId`/`cmsVersionId`
 * pair from whichever request populated that cache entry. That would incorrectly show preview
 * content -- or the wrong version's preview content -- to a visitor who never asked for it.
 * Same class of bug as ECOMM-11367 (`use-product-review.ts`), same fix: `router.asPath` is
 * derived from the actual browser URL (once `router.isReady`), not from the cached hydration
 * payload, so it reflects this specific navigation regardless of what the CDN served.
 */
export function useCmsPreviewVersionId(expectedModelId: string): string | undefined {
  const router = useRouter();
  const searchParams = router?.isReady ? parseAsPathSearchParams(router.asPath) : undefined;
  return getMatchingPreviewVersionId(searchParams, expectedModelId);
}

type CmsVersionPreview = {
  data: Data | undefined;
  isPreviewing: boolean;
  loading: boolean;
};

/**
 * Fetches preview data only when both URL parameters are valid for this renderer's model. The
 * version's `data` is untyped JSON on the wire (same as the resolve-by-targeting read) -- run it
 * through `isPuckData` here so both fetch paths converge on the one shared `Data | undefined`
 * shape `useCmsResolvedContent` returns, regardless of which source served it.
 */
export function useCmsVersionPreview(expectedModelId: string, enterpriseId: string | undefined): CmsVersionPreview {
  const versionId = useCmsPreviewVersionId(expectedModelId);
  const isPreviewing = Boolean(versionId);
  const { data, loading } = useQuery(CmsContentVersionDocument, {
    variables: { enterpriseId: enterpriseId ?? '', versionId: versionId ?? '' },
    skip: !enterpriseId || !versionId,
  });

  const versionData = data?.cmsContentVersion?.data;

  return {
    data: isPuckData(versionData) ? versionData : undefined,
    isPreviewing,
    loading,
  };
}
